/**
 * Авансы выданные и полученные с зачётом по актам; гарантийные удержания (ФН-06).
 */
import {
  ADVANCE_SIDES,
  type AdvanceRow,
  type AdvancesResponse,
  RETENTION_STATUSES,
  type RetentionRow,
  type RetentionsResponse,
  seesMoney,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date, tenge, todayLocal } from '../../lib/format';
import { projectsQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';

const num = (v: string) => Number(v.replace(/\s/g, '').replace(',', '.'));

export function AdvancesView({ projectId, contractId }: { projectId?: string; contractId?: string }) {
  const me = useMe();
  const money = seesMoney(me.access, 'acts_invoices');
  const qc = useQueryClient();
  const projects = useQuery({ ...projectsQuery('all'), enabled: money && !contractId });
  const [pid, setPid] = useState(projectId ?? '');
  const qKey = ['finance', 'advances', pid || 'all', contractId ?? ''];
  const rKey = ['finance', 'retentions', pid || 'all', contractId ?? ''];
  const adv = useQuery({
    queryKey: qKey,
    queryFn: () =>
      api<AdvancesResponse>('/finance/advances', {
        query: { projectId: pid || undefined, contractId },
      }),
    enabled: money,
  });
  const ret = useQuery({
    queryKey: rKey,
    queryFn: () =>
      api<RetentionsResponse>('/finance/retentions', {
        query: { projectId: pid || undefined, contractId },
      }),
    enabled: money,
  });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['finance', 'advances'] });
    void qc.invalidateQueries({ queryKey: ['finance', 'retentions'] });
  };

  if (!money)
    return (
      <EmptyState title="Авансы и удержания — с суммами">
        Раздел показывают сотрудникам с уровнем денег «Акты и счета» или выше и правом видеть финансы.
      </EmptyState>
    );
  if (adv.isPending || ret.isPending) return <Spinner />;
  if (adv.isError) return <ErrorBox error={adv.error} />;
  if (ret.isError) return <ErrorBox error={ret.error} />;
  const a = adv.data!;
  const r = ret.data!;

  return (
    <div className="flex flex-col gap-4">
      {!contractId ? (
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Проект" className="min-w-[200px]">
            <Select value={pid} onChange={(e) => setPid(e.target.value)}>
              <option value="">Все доступные</option>
              {(projects.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <Panel title="Авансы выданные">
          <div className="flex flex-col gap-1 p-5 text-[14px]">
            <Row label="Выдано" v={a.totals.issued.amount} />
            <Row label="Зачтено по актам" v={a.totals.issued.offset} />
            <Row label="Остаток" v={a.totals.issued.remaining} strong />
          </div>
        </Panel>
        <Panel title="Авансы полученные">
          <div className="flex flex-col gap-1 p-5 text-[14px]">
            <Row label="Получено" v={a.totals.received.amount} />
            <Row label="Зачтено по актам" v={a.totals.received.offset} />
            <Row label="Остаток" v={a.totals.received.remaining} strong />
          </div>
        </Panel>
      </div>

      <Panel
        title="Реестр авансов"
        meta={a.can.edit ? undefined : 'только просмотр'}
        actions={
          a.can.edit && contractId ? <NewAdvanceButton contractId={contractId} onSaved={invalidate} /> : null
        }
      >
        {!a.rows.length ? (
          <EmptyState title="Авансов пока нет">
            {a.can.edit
              ? 'Финансовый менеджер вносит выданный или полученный аванс по подписанному договору.'
              : 'Записи появятся, когда финансовый менеджер внесёт аванс.'}
          </EmptyState>
        ) : (
          <ScrollX>
            <table className="w-full min-w-[720px] text-left text-[14px]">
              <thead className="text-[12px] text-ink-2">
                <tr>
                  <th className="py-2.5 pr-3 pl-5">Договор</th>
                  <th className="py-2.5 pr-3">Сторона</th>
                  <th className="py-2.5 pr-3 text-right">Сумма</th>
                  <th className="py-2.5 pr-3 text-right">Зачтено</th>
                  <th className="py-2.5 pr-3 text-right">Остаток</th>
                  <th className="py-2.5 pr-5">Дата</th>
                </tr>
              </thead>
              <tbody>
                {a.rows.map((row) => (
                  <AdvanceLine key={row.id} row={row} canEdit={a.can.edit} onSaved={invalidate} />
                ))}
              </tbody>
            </table>
          </ScrollX>
        )}
      </Panel>

      <Panel
        title="Гарантийные удержания"
        meta={
          r.totals.overdue
            ? `просрочено к возврату ${tenge(r.totals.overdue)}`
            : `удерживается ${tenge(r.totals.held)}`
        }
        actions={
          r.can.edit && contractId ? (
            <NewRetentionButton contractId={contractId} onSaved={invalidate} />
          ) : null
        }
      >
        {!r.rows.length ? (
          <EmptyState title="Удержаний пока нет">
            Удержание с датой планового возврата вносит финансовый менеджер.
          </EmptyState>
        ) : (
          <ScrollX>
            <table className="w-full min-w-[720px] text-left text-[14px]">
              <thead className="text-[12px] text-ink-2">
                <tr>
                  <th className="py-2.5 pr-3 pl-5">Договор</th>
                  <th className="py-2.5 pr-3 text-right">Сумма</th>
                  <th className="py-2.5 pr-3">Возврат</th>
                  <th className="py-2.5 pr-3">Статус</th>
                  <th className="py-2.5 pr-5">Акт</th>
                </tr>
              </thead>
              <tbody>
                {r.rows.map((row) => (
                  <RetentionLine key={row.id} row={row} canEdit={r.can.edit} onSaved={invalidate} />
                ))}
              </tbody>
            </table>
          </ScrollX>
        )}
      </Panel>
    </div>
  );
}

function Row({ label, v, strong }: { label: string; v: number; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-ink-2">{label}</span>
      <span className={strong ? 'num font-semibold' : 'num'}>{tenge(v)}</span>
    </div>
  );
}

function AdvanceLine({ row, canEdit, onSaved }: { row: AdvanceRow; canEdit: boolean; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t border-line align-top">
        <td className="py-2.5 pr-3 pl-5">
          <Link to="/contracts/$contractId" params={{ contractId: row.contractId }} className="text-accent">
            {row.contractNumber}
          </Link>
          <div className="text-[12px] text-ink-2">
            {row.projectCode} · {row.counterpartyName}
          </div>
          {row.offsets.length ? (
            <ul className="mt-1 text-[12px] text-ink-2">
              {row.offsets.map((o) => (
                <li key={o.id}>
                  зачёт {tenge(o.amount)} от {date(o.offsetOn)}
                  {o.actNumber ? ` · акт ${o.actNumber}` : ''}
                </li>
              ))}
            </ul>
          ) : null}
        </td>
        <td className="py-2.5 pr-3">{ADVANCE_SIDES[row.side]}</td>
        <td className="num py-2.5 pr-3 text-right">{tenge(row.amount)}</td>
        <td className="num py-2.5 pr-3 text-right">{tenge(row.offset)}</td>
        <td className="num py-2.5 pr-3 text-right font-medium">{tenge(row.remaining)}</td>
        <td className="py-2.5 pr-5">
          <div>{row.paidOn ? date(row.paidOn) : '—'}</div>
          {canEdit && row.remaining > 0 ? (
            <Button
              type="button"
              variant="ghost"
              className="mt-1 min-h-11 px-0"
              onClick={() => setOpen(true)}
            >
              Зачесть по акту
            </Button>
          ) : null}
        </td>
      </tr>
      {open ? (
        <OffsetDialog
          advanceId={row.id}
          remaining={row.remaining}
          open={open}
          onOpenChange={setOpen}
          onSaved={() => {
            setOpen(false);
            onSaved();
          }}
        />
      ) : null}
    </>
  );
}

function RetentionLine({
  row,
  canEdit,
  onSaved,
}: {
  row: RetentionRow;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const qcMark = useMutation({
    mutationFn: () =>
      api<RetentionRow>(`/finance/retentions/${row.id}`, {
        method: 'PATCH',
        body: { status: 'returned', returnedOn: todayLocal() },
      }),
    onSuccess: () => {
      toast('Удержание отмечено возвращённым');
      onSaved();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Не удалось отметить возврат', 'danger'),
  });
  return (
    <tr className="border-t border-line">
      <td className="py-2.5 pr-3 pl-5">
        <Link to="/contracts/$contractId" params={{ contractId: row.contractId }} className="text-accent">
          {row.contractNumber}
        </Link>
        <div className="text-[12px] text-ink-2">
          {row.projectCode} · {row.counterpartyName}
        </div>
      </td>
      <td className="num py-2.5 pr-3 text-right">{tenge(row.amount)}</td>
      <td className="py-2.5 pr-3">
        {date(row.returnPlanned)}
        {row.overdue ? (
          <Badge tone="danger" className="ml-2">
            просрочено
          </Badge>
        ) : null}
      </td>
      <td className="py-2.5 pr-3">
        {RETENTION_STATUSES[row.status]}
        {row.returnedOn ? <div className="text-[12px] text-ink-2">{date(row.returnedOn)}</div> : null}
        {canEdit && row.status === 'held' ? (
          <Button
            type="button"
            variant="ghost"
            className="mt-1 min-h-11 px-0"
            disabled={qcMark.isPending}
            onClick={() => qcMark.mutate()}
          >
            Отметить возврат
          </Button>
        ) : null}
      </td>
      <td className="py-2.5 pr-5 text-ink-2">{row.actNumber ?? '—'}</td>
    </tr>
  );
}

function OffsetDialog({
  advanceId,
  remaining,
  open,
  onOpenChange,
  onSaved,
}: {
  advanceId: string;
  remaining: number;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const [amount, setAmount] = useState(String(remaining));
  const [offsetOn, setOffsetOn] = useState(todayLocal());
  const [actNumber, setActNumber] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api(`/finance/advances/${advanceId}/offsets`, {
        method: 'POST',
        body: {
          amount: num(amount),
          offsetOn,
          actNumber: actNumber.trim() || null,
        },
      }),
    onSuccess: () => {
      toast('Зачёт записан');
      onSaved();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Не удалось зачесть', 'danger'),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Зачёт аванса по акту">
      <div className="flex flex-col gap-3">
        <p className="text-[13px] text-ink-2">Остаток аванса: {tenge(remaining)}</p>
        <Field label="Сумма зачёта">
          <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
        </Field>
        <Field label="Дата акта">
          <Input type="date" value={offsetOn} onChange={(e) => setOffsetOn(e.target.value)} />
        </Field>
        <Field label="Номер акта">
          <Input value={actNumber} onChange={(e) => setActNumber(e.target.value)} placeholder="АВР-…" />
        </Field>
        <div className="flex flex-wrap gap-2">
          <Button type="button" disabled={m.isPending} onClick={() => m.mutate()}>
            Зачесть
          </Button>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function NewAdvanceButton({ contractId, onSaved }: { contractId: string; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [side, setSide] = useState<'issued' | 'received'>('issued');
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(todayLocal());
  const m = useMutation({
    mutationFn: () =>
      api('/finance/advances', {
        method: 'POST',
        body: { contractId, side, amount: num(amount), paidOn: paidOn || null },
      }),
    onSuccess: () => {
      toast('Аванс записан');
      setOpen(false);
      onSaved();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Не удалось записать аванс', 'danger'),
  });
  return (
    <>
      <Button type="button" variant="ghost" className="min-h-11" onClick={() => setOpen(true)}>
        Добавить аванс
      </Button>
      <Dialog open={open} onOpenChange={setOpen} title="Новый аванс">
        <div className="flex flex-col gap-3">
          <Field label="Сторона">
            <Select value={side} onChange={(e) => setSide(e.target.value as 'issued' | 'received')}>
              <option value="issued">{ADVANCE_SIDES.issued}</option>
              <option value="received">{ADVANCE_SIDES.received}</option>
            </Select>
          </Field>
          <Field label="Сумма">
            <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
          </Field>
          <Field label="Дата">
            <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Button type="button" disabled={m.isPending || !amount} onClick={() => m.mutate()}>
              Сохранить
            </Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Отмена
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

function NewRetentionButton({ contractId, onSaved }: { contractId: string; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [returnPlanned, setReturnPlanned] = useState('');
  const [actNumber, setActNumber] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api('/finance/retentions', {
        method: 'POST',
        body: {
          contractId,
          amount: num(amount),
          returnPlanned,
          withheldOn: todayLocal(),
          actNumber: actNumber.trim() || null,
        },
      }),
    onSuccess: () => {
      toast('Удержание записано');
      setOpen(false);
      onSaved();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Не удалось записать удержание', 'danger'),
  });
  return (
    <>
      <Button type="button" variant="ghost" className="min-h-11" onClick={() => setOpen(true)}>
        Добавить удержание
      </Button>
      <Dialog open={open} onOpenChange={setOpen} title="Гарантийное удержание">
        <div className="flex flex-col gap-3">
          <Field label="Сумма">
            <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
          </Field>
          <Field label="Дата планового возврата">
            <Input type="date" value={returnPlanned} onChange={(e) => setReturnPlanned(e.target.value)} />
          </Field>
          <Field label="Номер акта">
            <Input value={actNumber} onChange={(e) => setActNumber(e.target.value)} />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={m.isPending || !amount || !returnPlanned}
              onClick={() => m.mutate()}
            >
              Сохранить
            </Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Отмена
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
